import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

// API endpoint constants
const CAMPAIGN_API = {
  list: 'api/campaigns/list',
  details: 'api/campaigns',
  create: 'api/campaigns',
  update: 'api/campaigns',
  delete: 'api/campaigns',
  trigger: 'api/campaigns',
};

export interface CampaignListType {
  campaignName: string;
  campaignType: string;
  configId: string;
  createdAt: string;
  id: string;
  teamId: string | null;
  tenantId: string;
  updatedAt: string;
}

export const campaignApi = createApi({
  reducerPath: 'campaignApi',
  baseQuery,
  endpoints: (builder) => ({
    // ----------------------------------------------------------------------
    // NOTE FOR DEVELOPERS:
    // Please maintain the following order for endpoints ensuring CRUD consistency:
    // 1. List (Filter)
    // 2. Details
    // 3. Create
    // 4. Update
    // 5. Delete
    // 6. Trigger
    // ----------------------------------------------------------------------

    getCampaignList: builder.mutation({
      query: (campaignPayload) => ({
        url: CAMPAIGN_API.list,
        method: 'POST',
        body:
          Object.keys(campaignPayload).length > 0
            ? campaignPayload
            : {
                filters: [],
              },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { campaign?: { list?: CampaignListType[]; total: number } };
      }) => ({
        list: response?.data?.campaign?.list ?? [],
        total: response?.data?.campaign?.total ?? 0,
      }),
    }),

    getCampaignDetails: builder.mutation({
      query: (id: string) => ({
        url: `${CAMPAIGN_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data: any }) => ({
        data: { campaign: response.data },
      }),
    }),

    addCampaign: builder.mutation({
      query: (campaignPayload) => ({
        url: CAMPAIGN_API.create,
        method: 'POST',
        body: campaignPayload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    updateCampaign: builder.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `${CAMPAIGN_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    deleteCampaign: builder.mutation({
      query: (id: string) => ({
        url: `${CAMPAIGN_API.delete}/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    triggerCampaign: builder.mutation({
      query: (campaignId: string) => ({
        url: `${CAMPAIGN_API.trigger}/${campaignId}/trigger`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useGetCampaignListMutation,
  useGetCampaignDetailsMutation,
  useAddCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useTriggerCampaignMutation,
} = campaignApi;
